/* eslint-disable sonarjs/cognitive-complexity */
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { parseBoolean, getDateRange, getIconPath } from '@tcp/core/src/utils';
import { setSessionStorage, getSessionStorage } from '@tcp/core/src/utils/utils.web';
import EddComponent from '@tcp/core/src/components/features/CnC/common/molecules/EddComponent';
import { getSelectedShipmentMsg } from '@tcp/core/src/utils/utils';
import Row from '@tcp/core/src/components/common/atoms/Row';
import Col from '@tcp/core/src/components/common/atoms/Col';
import {
  getLabelFromCopyOrFromTranslation,
  getLabelWithDefaultValue,
} from '@tcp/core/src/utils/labels/labelUtilty';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Image from '@tcp/core/src/components/common/atoms/Image';
import LabeledRadioButton from '@tcp/core/src/components/common/atoms/LabeledRadioButton';
import UpdatingSpinner from '@tcp/core/src/components/features/CnC/CartItemTile/molecules/CartItemTile/views/UpdatingSpinner.view.jsx';
import styles from '../styles/ShipmentMethods.style';
import withStyles from '../../../../../../common/hoc/withStyles';
import ShippingMethodSkeleton from './ShipmentMethodSkeleton.view';
import { shouldAutoUpdateMethodSelection } from '../../../../SinglePageCheckout/container/Shipping/Shipping.utils';
import { checkForFreeShipping } from '../../../../BagPage/views/Bagpage.utils';
import BAGPAGE_CONSTANTS from '../../../../BagPage/BagPage.constants';
import ShippingLabels, {
  CHECKOUT_SHIPPING,
  ShipmentStandard,
  ShipmentExpress,
  ShipmentRush,
  ShipmentUSPS,
  ShipmentFree,
  ShipmentEstimatedDelivery,
  ShipmentRangeDelivery,
  ShipmentStandardShipping,
  ShipmentExpressShipping,
  ShipmentRushShipping,
} from '../../../../SinglePageCheckout/molecules/Shipping/Shipping.labels';

export const getShipmentLabels = (fromShipping) => {
  const shippingLabels = getLabelFromCopyOrFromTranslation(CHECKOUT_SHIPPING, ShippingLabels);
  if (fromShipping) {
    return {
      '001': getLabelWithDefaultValue(shippingLabels, ShipmentStandardShipping),
      '002': getLabelWithDefaultValue(shippingLabels, ShipmentExpressShipping),
      '003': getLabelWithDefaultValue(shippingLabels, ShipmentRushShipping),
      '004': getLabelWithDefaultValue(shippingLabels, ShipmentStandardShipping),
      '005': getLabelWithDefaultValue(shippingLabels, ShipmentExpressShipping),
      '006': getLabelWithDefaultValue(shippingLabels, ShipmentRushShipping),
      '008': getLabelWithDefaultValue(shippingLabels, ShipmentUSPS),
      '009': getLabelWithDefaultValue(shippingLabels, ShipmentStandardShipping),
      PGND_8: getLabelWithDefaultValue(shippingLabels, ShipmentStandardShipping),
      CGND_8: getLabelWithDefaultValue(shippingLabels, ShipmentStandardShipping),
      PEXP_15: getLabelWithDefaultValue(shippingLabels, ShipmentExpressShipping),
    };
  }
  return {
    '001': getLabelWithDefaultValue(shippingLabels, ShipmentStandard),
    '002': getLabelWithDefaultValue(shippingLabels, ShipmentExpress),
    '003': getLabelWithDefaultValue(shippingLabels, ShipmentRush),
    '004': getLabelWithDefaultValue(shippingLabels, ShipmentStandard),
    '005': getLabelWithDefaultValue(shippingLabels, ShipmentExpress),
    '006': getLabelWithDefaultValue(shippingLabels, ShipmentRush),
    '008': getLabelWithDefaultValue(shippingLabels, ShipmentUSPS),
    '009': getLabelWithDefaultValue(shippingLabels, ShipmentStandard),
    PGND_8: getLabelWithDefaultValue(shippingLabels, ShipmentStandard),
    CGND_8: getLabelWithDefaultValue(shippingLabels, ShipmentStandard),
    PEXP_15: getLabelWithDefaultValue(shippingLabels, ShipmentExpress),
  };
};

/*
  Translation for AK and HI is not trnalsating. So we are splitting the state code before translating.
*/
export const getShippingTitle = (shippingLabels, titles, id) => {
  const shippingSpeed = titles?.[0];
  let translatedText = shippingSpeed;
  if (shippingLabels && shippingSpeed && id) {
    const shippingCode = shippingLabels[id] || '';
    const shippingTexts = shippingSpeed.trim().split(' ');

    if (shippingTexts.length > 1 && shippingCode) {
      shippingTexts[shippingTexts.length - 1] = shippingCode;
      translatedText = shippingTexts.join(' ');
    } else {
      translatedText = shippingCode;
    }
  }
  return translatedText || shippingSpeed || '';
};

export const getRangeDateText = (shippingSpeed) => {
  const daysMatch = shippingSpeed?.match(/\d+/g);
  if (daysMatch && daysMatch.length === 1) {
    const rangeDateText = getLabelFromCopyOrFromTranslation(
      `${CHECKOUT_SHIPPING}.${ShipmentEstimatedDelivery}`,
      ShippingLabels
    );
    return rangeDateText.replace('_maxNoOfDays_', daysMatch[0]);
  }
  if (daysMatch && daysMatch.length >= 2) {
    const rangeDateText = getLabelFromCopyOrFromTranslation(
      `${CHECKOUT_SHIPPING}.${ShipmentRangeDelivery}`,
      ShippingLabels
    );

    return rangeDateText
      .replace('_minNoOfDays_', daysMatch[0])
      .replace('_maxNoOfDays_', daysMatch[1]);
  }
  return shippingSpeed;
};

const getEddSessionState = (isEddABTest) => {
  const isEddSessionEnabled = parseBoolean(getSessionStorage('EDD_ENABLED'));
  if (!isEddSessionEnabled) {
    setSessionStorage({ key: 'EDD_ENABLED', value: isEddABTest });
  }
  return isEddABTest || isEddSessionEnabled;
};

const getShipmentMethodOptionClass = (id, currentShipmentId) =>
  id === currentShipmentId ? 'checked' : '';

const renderSelectedMsgView = (obj) => {
  const { selectedShipment, isEDD, isEddABStateFlag } = obj;
  return (
    isEDD &&
    isEddABStateFlag && (
      <BodyCopy
        fontFamily="secondary"
        fontSize="fs12"
        fontWeight="semibold"
        className="elem-mt-XS"
        textAlign="center"
      >
        <EddComponent
          selectedShipment={selectedShipment.code}
          isRangeDate
          fontSize="fs12"
          textAlign="left"
          spc
        />
      </BodyCopy>
    )
  );
};

const getDisplayName = (displayName) => {
  return displayName ? displayName.split('-') : [];
};

const ShipmentMethods = ({
  shipmentMethods,
  selectedShipmentId,
  className,
  shipmentHeader,
  isLoadingShippingMethods,
  isUpdatingShipmentMethods,
  isEDD,
  isEddABTest,
  currentShipmentId,
  dispatchFormFieldChange,
  orderEddDates,
  sectionMode,
  submitSaveAndContinueShipping,
  isShippingViewV2Flow,
  fromShippingCollapsedSection,
  shippingFormValues,
  shippingState,
  holidayMessageEnabled,
  freeShippingThershold,
  shippingTotal,
  isDynamicFreeShippingEnabled,
  shipments,
  orderType,
  productTotal,
  isShippingMethodsOnShippingEnabled,
}) => {
  const selectedShipment =
    shipmentMethods && shipmentMethods.find((method) => method.id === selectedShipmentId);
  const selectedShipmentMsg = getSelectedShipmentMsg(selectedShipment, shipmentMethods);
  const isEddABStateFlag = parseBoolean(getEddSessionState(isEddABTest));
  const tickMarkImage = getIconPath('tick-mark');
  const checkIfSameAddress = shippingFormValues?.state === shippingState;

  useEffect(() => {
    if (shouldAutoUpdateMethodSelection({ sectionMode, shipmentMethods, currentShipmentId })) {
      // Invalid currentShipmentId; Update current shipment id to default method
      // If default not present, take the first method
      const autoShipmentSelection = shipmentMethods.find((s) => s.isDefault) || shipmentMethods[0];
      dispatchFormFieldChange('shipmentMethods.shippingMethodId', autoShipmentSelection?.id);
    }
  }, [currentShipmentId, shipmentMethods, sectionMode]);

  const handleOnChange = (e) => {
    if (isShippingViewV2Flow) {
      dispatchFormFieldChange('shipmentMethods.shippingMethodId', e?.target?.value);
      submitSaveAndContinueShipping(e?.target?.value, isShippingMethodsOnShippingEnabled);
    }
  };

  const shippingLabels = useMemo(() => getShipmentLabels(), []);

  return (
    <>
      <BodyCopy
        fontFamily="secondary"
        fontSize="fs16"
        fontWeight="extrabold"
        dataLocator="shippingMethods"
      >
        {shipmentHeader}
      </BodyCopy>
      {isLoadingShippingMethods && !fromShippingCollapsedSection ? (
        <ShippingMethodSkeleton />
      ) : (
        <>
          {!fromShippingCollapsedSection &&
            isEDD &&
            checkIfSameAddress &&
            holidayMessageEnabled && (
              <EddComponent
                selectedShipment={selectedShipment?.code}
                isRangeDate
                spc
                myBagSpc
                textAlign="left"
                isFromShippingExpanded={true}
              />
            )}
          <Row fullBleed className={`shipping-method ${className}`}>
            {shipmentMethods &&
              shipmentMethods.length > 0 &&
              shipmentMethods.map((option) => {
                const { id, price, shippingSpeed, code, displayName } = option || {};
                let amount;
                if (BAGPAGE_CONSTANTS.FREE_SHIPMENT_CODES.includes(code)) {
                  const isFreeShippingEligible = checkForFreeShipping({
                    shippingTotal,
                    isDynamicFreeShippingEnabled,
                    shipments,
                    freeShippingThershold,
                    selectedShipmentCode: code,
                    orderType,
                    productTotal,
                    isShippingMethods: true,
                  });
                  amount = isFreeShippingEligible
                    ? getLabelFromCopyOrFromTranslation(
                        `${CHECKOUT_SHIPPING}.${ShipmentFree}`,
                        ShippingLabels
                      )
                    : `$${price}`;
                } else {
                  amount =
                    price > 0
                      ? `$${price}`
                      : getLabelFromCopyOrFromTranslation(
                          `${CHECKOUT_SHIPPING}.${ShipmentFree}`,
                          ShippingLabels
                        );
                }
                const titles = getDisplayName(displayName);
                const title = getShippingTitle(shippingLabels, titles, id);
                const rangeDateText = getRangeDateText(shippingSpeed);
                const rangeDate = orderEddDates ? getDateRange(orderEddDates, code, '', true) : '';
                return (
                  <BodyCopy
                    component="div"
                    className={`ship-option-container ${getShipmentMethodOptionClass(
                      id,
                      currentShipmentId
                    )}`}
                  >
                    <Field
                      component={LabeledRadioButton}
                      key={id}
                      selectedValue={id}
                      title={title}
                      subtitle={amount}
                      name="shippingMethodId"
                      className="estimated-shipping-size"
                      midtitle={rangeDate || rangeDateText}
                      topPosition="12px"
                      spc
                      onChange={(e) => handleOnChange(e)}
                    />
                    {id === currentShipmentId && (
                      <>
                        {isUpdatingShipmentMethods && fromShippingCollapsedSection && (
                          <div className="spinner-style">
                            <UpdatingSpinner />
                          </div>
                        )}
                        <div className="selected-tile-tick">
                          <div className="checkmark">
                            <Image
                              src={tickMarkImage}
                              alt="tick-mark-icon"
                              className="tick-mark-image"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </BodyCopy>
                );
              })}
          </Row>
          {selectedShipment && (
            <Row fullBleed>
              <Col colSize={{ small: 6, medium: 0, large: 0 }}>
                {renderSelectedMsgView({
                  selectedShipment,
                  selectedShipmentMsg,
                  isEDD,
                  isEddABStateFlag,
                })}
              </Col>
            </Row>
          )}
        </>
      )}
    </>
  );
};

ShipmentMethods.propTypes = {
  shipmentMethods: PropTypes.shape([]),
  selectedShipmentId: PropTypes.string,
  className: PropTypes.string,
  shipmentHeader: PropTypes.string,
  isLoadingShippingMethods: PropTypes.bool,
  isUpdatingShipmentMethods: PropTypes.bool,
  isEDD: PropTypes.bool,
  isEddABTest: PropTypes.bool,
  currentShipmentId: PropTypes.string,
  dispatchFormFieldChange: PropTypes.func.isRequired,
  orderEddDates: PropTypes.shape({}),
  sectionMode: PropTypes.string.isRequired,
  fromShippingCollapsedSection: PropTypes.bool,
};
ShipmentMethods.defaultProps = {
  shipmentMethods: null,
  selectedShipmentId: ' ',
  className: '',
  shipmentHeader: '',
  isLoadingShippingMethods: false,
  isUpdatingShipmentMethods: false,
  isEDD: false,
  isEddABTest: false,
  currentShipmentId: '',
  orderEddDates: {},
  fromShippingCollapsedSection: false,
};
export default withStyles(ShipmentMethods, styles);
export { ShipmentMethods as ShipmentMethodsVanilla };
